/* eslint-disable react/no-unknown-property */
/* eslint-disable max-len */
/* eslint-disable @typescript-eslint/no-use-before-define */
import React from 'react';
import PageLoader from 'components/Loaders/PageLoader';
import { useFormik } from 'formik';
import { useNotification, useVMThunkDispatch } from 'hooks';
import { Trans, useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { ReactComponent as ArrowLeft } from 'assets/icons_3/arrows/arrow-left.svg';
import { ReactComponent as ServerIcon } from 'assets/icons_3/server.svg';
import { RootState } from 'store';
import { vmActions } from 'store/virtual-machine';
import { IVMEditForm } from 'store/virtual-machine/types/forms.types';
import {
  DiskType,
  IVMState,
  LimitedVMParam,
} from 'store/virtual-machine/types/state.types';
import VirtualMachine from 'store/virtual-machine/types/virtual-machine';

import * as yup from 'yup';
import vmService from 'services/api/customer/vm-service';
import PageLayout from 'components/Layouts/PageLayout/PageLayout';
import useVMCreateLimits, { ReservedParams } from 'hooks/use-vm-create-limits';
import useAsyncFetch from 'hooks/async-fetch';
import ResourcesLimit from 'components/VirtualMachine/ResourcesLimits';
import useMoneyFormatter from 'hooks/money-formatter';
import RouterLinkButton from 'components/UI_kit_3/Button/RouterLinkButton';
import LinkButton from 'components/UI_kit_3/Button/LinkButton';
import Card from 'components/UI_kit_3/Card/Card';
import FormText from 'components/UI_kit_3/FormText/FormText';
import RangeInput from 'components/UI_kit_3/RangeInput/RangeInput';
import classNames from 'classnames';
import { ReactComponent as QuestionIcon } from 'assets/icons_3/question-circle.svg';
import Tooltip from 'components/UI_kit_3/Popover/Tooltip/Tooltip';
import ButtonWithConfirm from 'components/UI_kit_3/Button/ButtonWithConfirm/ButtonWithConfirm';
import useDynamicTranslation from 'hooks/use-dynamic-translation';
import Button from 'components/UI_kit_3/Button/Button';
import styles from './vm.module.scss';

export enum VmEditType {
  NoEdit,
  Name,
  Description,
  Config,
  Dns,
}

const VMEditPage = () => {
  const [t] = useTranslation();
  const notification = useNotification();
  const moneyFormatter = useMoneyFormatter();
  const thunkVMDispatch = useVMThunkDispatch();
  const getDynamicTranslation = useDynamicTranslation();
  const navigate = useNavigate();
  const vmState: IVMState = useSelector((state: RootState) => state.virtualMachine);
  const dispatch = useDispatch();
  const virtualMachine = vmState.single;
  const params = useParams();
  const diskType = virtualMachine?.disks[0].diskType || DiskType.HDD;

  const reservedParams: ReservedParams = {
    cpu: -1 * (virtualMachine?.cpuCores || 0),
    ram: -1 * (virtualMachine?.ram || 0),
    ssd_disk: virtualMachine?.diskType === DiskType.SSD ? -1 * (virtualMachine?.diskSize || 0) : 0,
    hdd_disk: virtualMachine?.diskType === DiskType.HDD ? -1 * (virtualMachine?.diskSize || 0) : 0,
    nvme_disk: virtualMachine?.diskType === DiskType.NVME ? -1 * (virtualMachine?.diskSize || 0) : 0,
  };

  const [vmCreateLimits, calculateLimits] = useVMCreateLimits(diskType, vmState.single?.cpuCores || 2);
  const { currency } = vmCreateLimits.cpu;

  const [, loading] = useAsyncFetch<[string | undefined], VirtualMachine>({
    asyncFunc: (id) => {
      if (id) {
        return thunkVMDispatch(vmActions.fetchVMById(id));
      }
      return Promise.resolve();
    },
    immediate: true,
    initialParams: [params.id],
  });

  const [handleEdit, editLoading] = useAsyncFetch<[IVMEditForm], string | undefined>({
    async asyncFunc(form: IVMEditForm): Promise<string | undefined> {
      const res = await vmService.editVMConfig(virtualMachine?.id || '-1', VirtualMachine.getEditBody(form, editType));
      return res.data.data.id;
    },
    errorNote: true,
    // successNote: t('vm.success_change'),
    // eslint-disable-next-line @typescript-eslint/no-shadow
    successHandler(id, params) {
      const [form] = params ?? [];
      if (id) {
        const message = form?.reboot
          ? t('vm.success_change')
          : t('vm.success_special_change');
        notification.success('', message);
        navigate(`/services/servers/${id}`);
      }
    },
    // successHandler(id) {
    //   if (id) {
    //     navigate(`/services/servers/${id}`);
    //   }
    // },
  });

  const {
    handleSubmit,
    submitForm,
    handleChange,
    values,
    touched,
    errors,
    dirty,
    isValid,
    setFieldValue,
    submitCount,
    initialValues,
    validateForm,
  } = useFormik({
    validationSchema: yup.object().shape({
      vmName: yup.string().required(t('form.error.required')).matches(/^[a-zA-Z0-9-]+$/g, t('vm.params.error.name')),
      // description: yup.string().required(t('form.error.required')).max(30, t('vm.params.error.desc')),
      ram: yup.number().typeError(t('form.error.wrong_value')).required(t('form.error.required')).test('ramSize', t('form.error.wrong_value'), (value) => {
        if (value === undefined) return false;
        return (value >= vmCreateLimits.ram.min) && (value <= vmCreateLimits.ram.max);
      }),
      cpuCores: yup.number().typeError(t('form.error.wrong_value')).required(t('form.error.required')),
    }),
    onSubmit: handleEdit,
    initialValues: virtualMachine?.editForm || {
      vmName: '',
      description: '',
      cpuCores: 0,
      ram: 0,
      reboot: false,
    },
    enableReinitialize: true,
    validateOnBlur: true,
  });

  const prices = React.useMemo(() => ({
    cpu: { value: values.cpuCores * (vmState.paramLimits?.cpu_cores?.price || 0), currency: vmState.paramLimits.cpu_cores?.currencyCode },
    ram: { value: values.ram * (vmState.paramLimits?.ram?.price || 0), currency: vmState.paramLimits.ram?.currencyCode },
    publicIp: { value: (virtualMachine?.externalIpAddress && vmState.paramLimits.public_ip?.price) || 0, currency: vmState.paramLimits.public_ip?.currencyCode },
    disks: {
      value: virtualMachine?.disks.reduce<number>((sum, disk) => {
        if (disk.diskType === DiskType.HDD) {
          return sum + (disk.size * (vmState.paramLimits?.hdd_size?.price || 0));
        }
        if (disk.diskType === DiskType.SSD) {
          return sum + (disk.size * (vmState.paramLimits?.ssd_size?.price || 0));
        }
        return sum;
      }, 0) || 0,
      currency: vmState.paramLimits.hdd_size?.currencyCode,
    },
  }), [values, vmState]);

  const handleSelectCpu = React.useCallback((value: number) => {
    setFieldValue('cpuCores', value);

    const limits = calculateLimits({
      cpu: value,
      diskType,
      reserved: reservedParams,
    });

    if (values.ram <= limits.ram.min) {
      setFieldValue('ram', limits.ram.min);
    } else if (values.ram >= limits.ram.max) {
      setFieldValue('ram', limits.ram.max);
    }
  }, [values.ram, diskType, virtualMachine]);

  React.useEffect(() => {
    const fields = Object.keys(errors).map((fieldName) => document.querySelector(`[name=${fieldName}]`) as HTMLElement);
    const fieldsSorted = fields.sort((a, b) => a.getBoundingClientRect().y
    - b.getBoundingClientRect().y);
    fieldsSorted[0]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [submitCount]);

  const editType = React.useMemo(() => {
    if (initialValues.cpuCores !== values.cpuCores || initialValues.ram !== values.ram) {
      return VmEditType.Config;
    }
    if (initialValues.vmName !== values.vmName) {
      return VmEditType.Name;
    }
    if (initialValues.description !== values.description) {
      return VmEditType.Description;
    }
    return VmEditType.NoEdit;
  }, [values]);

  React.useEffect(() => () => {
    dispatch(vmActions.clearSingleVM());
  }, []);

  React.useEffect(() => {
    calculateLimits({
      cpu: values.cpuCores,
      diskType,
      reserved: reservedParams,
    });
    validateForm();
  }, [vmState.paramLimits]);

  React.useEffect(() => {
    if (!virtualMachine) return;
    calculateLimits({
      cpu: virtualMachine.cpuCores,
      diskType,
      reserved: reservedParams,
    });
    validateForm();
  }, [vmState.paramLimits, virtualMachine?.id]);

  return (
    <PageLayout title={t('vm.editing')} className="page-container">
      <RouterLinkButton
        prefixIcon={<ArrowLeft />}
        variant="tertiary"
        to="../"
        className="mb-xl pl-0 align-self-start"
        qa-element="to-back"
      >
        {t('common.back')}
      </RouterLinkButton>
      <ResourcesLimit
        className="mb-4"
        limitsToShow={[LimitedVMParam.CPUCores, LimitedVMParam.RAM, LimitedVMParam.HDDSize,
          LimitedVMParam.SSDSize,
          LimitedVMParam.NVMESize,
          LimitedVMParam.LocalNetworks,
          LimitedVMParam.BackupQuota]}
      />
      {loading && <PageLoader />}
      {!loading && (
        <div className="flex gap-x-4xl justify-between">
          <form className="flex-grow-1" noValidate onSubmit={handleSubmit} id="vm-form">
            <h3 className="font-lg-semibold mb-xl">{t('backup.name_and_desc')}</h3>
            <FormText
              className="mb-3xl"
              type="text"
              name="vmName"
              id="name"
              label={t('vm.params.name')}
              autoComplete="false"
              value={values.vmName}
              maxLength={30}
              onChange={handleChange}
              error={errors.vmName}
              isInvalid={touched.vmName && !!errors.vmName}
              qa-element="vm-edit-name"
              qaElementFeedback="edit-vm-name-error"
            />
            <FormText
              type="text"
              className="mb-5xl"
              id="description"
              label={t('vm.params.desc')}
              name="description"
              autoComplete="false"
              value={values.description}
              maxLength={50}
              onInput={handleChange}
              error={errors.description}
              isInvalid={touched.description && !!errors.description}
              qa-element="vm-edit-desc"
              qaElementFeedback="edit-vm-desc-error"
            />
            <h3 className="font-md-semibold mb-3xl">
              {t('vm.resources')}
            </h3>
            <RangeInput
              id="cpu-cores"
              className="mb-4xl"
              value={values.cpuCores}
              label={`${t('vm.params.cpu_cores')}`}
              name="cpuCores"
              min={vmCreateLimits.cpu.min}
              max={vmCreateLimits.cpu.max}
              onChange={handleSelectCpu}
              qaElement="edit-vm-cpu-range"
            />
            <RangeInput
              id="ram"
              value={values.ram}
              label={`${t('vm.params.ram_2')}, ${t('common.gb')}`}
              name="ram"
              error={touched.ram ? errors.ram : undefined}
              min={vmCreateLimits.ram.min}
              max={vmCreateLimits.ram.max}
              onChange={(value) => {
                setFieldValue('ram', value);
              }}
              qaElement="edit-vm-ram-range"
            />
          </form>
          <div className={classNames('flex-grow-1 ', styles.price)}>
            <Card variant="light" className="mb-xl">
              <Card.Header>
                <Card.Title className="flex">
                  {t('vm.calculate_price')}
                  <Tooltip
                    trigger="click"
                    text={(
                      <Trans
                        i18nKey="common.price_for_informational_purposes"
                        components={{
                          price: <LinkButton variant="link" href="https://www.pro-data.tech/price" />,
                        }}
                      />
                  )}
                  >
                    <div className="ml-md text-quaternary-l"><QuestionIcon /></div>
                  </Tooltip>
                </Card.Title>
              </Card.Header>
              <Card.Body>
                <table>
                  <tbody>
                    <tr>
                      <td>
                        <span className="font-sm-bold">{values.cpuCores}</span>
                        {` ${t('vm.params.cpu')}`}
                        :
                      </td>
                      <td className="flex justify-content-end">{`${moneyFormatter(prices.cpu.value, currency)}`}</td>
                    </tr>
                    <tr>
                      <td>
                        <span className="font-sm-bold">{values.ram}</span>
                        {` ${t('vm.params.ram_2')}`}
                        :
                      </td>
                      <td className="flex justify-content-end">{`${moneyFormatter(prices.ram.value, currency)}`}</td>
                    </tr>
                    <tr>
                      <td>
                        {`${t('disk.self')} `}
                        {getDynamicTranslation(`disk.disk_types.${vmState.single?.diskType.toLowerCase()}`)}
                        :
                      </td>
                      <td className="text-end">{`${moneyFormatter(prices.disks.value, currency)}`}</td>
                    </tr>
                  </tbody>
                </table>
              </Card.Body>
              <Card.Footer>
                <div className="flex font-sm-semibold justify-content-between align-items-baseline mb-2xl">
                  <span>
                    {t('common.in_total')}
                    :
                  </span>
                  <span>{`${moneyFormatter(Object.values(prices).reduce((sum, val) => sum + val.value, 0), currency)}`}</span>
                </div>
                <div className="flex flex-column gap-xl ">
                  {editType === VmEditType.Config ? (
                    <ButtonWithConfirm
                      variant="primary"
                      className="flex-grow-1"
                      disabled={editLoading || !dirty || !isValid}
                      onConfirm={(isChecked) => {
                        setFieldValue('reboot', isChecked);
                        setTimeout(() => submitForm(), 0);
                      }}
                      header={t('common.alert')}
                      confirmTitle={t('common.submit')}
                      description={t('vm.server_will_be_rebooted')}
                      confirmCheckbox={editType === VmEditType.Config ? t('vm.confirm_reboot') : undefined}
                      Icon={<ServerIcon />}
                      qaElement="vm-edit-submit"
                    >
                      {t('vm.change_config')}
                    </ButtonWithConfirm>
                  )
                    : (<Button onClick={submitForm} disabled={editLoading || !dirty || !isValid}>{t('vm.change_config')}</Button>)}
                  <RouterLinkButton to="/services/servers" variant="secondary" className="flex-grow-1" qa-element="to-server-list">{t('common.cancel')}</RouterLinkButton>
                </div>
              </Card.Footer>
            </Card>
          </div>
        </div>
      )}
    </PageLayout>
  );
};

export default VMEditPage;
